import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getAccountOfficerAssignedCustomersWithRisk,
  getAllProducts,
  getAllProductType,
  getCartItems,
  addToCartThunk,
  updateCartItemThunk,
  deleteCartItemThunk,
  sendConfirmationOtp,
  verifyOtp,
  createOrderThunk,
  updateDeliveryPreferencesThunk,
  getAllFees,
  checkIfUserHasOrder,
  getCustomerByuserIdThunk,
  validatePromoCodeThunk,
} from "../../redux/thunk";
import {
  clearCart,
} from "../../redux/cart/cartSlice";
import {
  FiSearch,
  FiUser,
  FiShoppingCart,
  FiArrowLeft,
  FiX,
  FiPlus,
  FiMinus,
  FiCheckCircle,
  FiTrash2,
  FiFilter,
  FiShoppingBag,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiMapPin,
  FiPhone,
  FiEdit3,
} from "react-icons/fi";
import { cn } from "../../lib/utils";
import toast from "react-hot-toast";
import axios from "axios";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import FundWalletModal from "../../components/modals/FundWalletModal";
import DatePicker from "../../components/elements/DatePicker";
import { FeeItem } from "../../types/types";

const CreateOrder = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    assignedCustomers,
    assignedCustomersStatus,
    assignedCustomersTotal,
    assignedCustomersTotalPages
  } = useAppSelector(
    (store) => store.accountOfficer,
  );
  const {
    products,
    categories,
    status: productStatus,
  } = useAppSelector((store) => store.products);
  const {
    items: cartItems,
    totalAmount,
    totalQuantity,
  } = useAppSelector((store) => store.cart);
  const { token } = useAppSelector((store) => store.auth);

  // Extract category list safely from various possible API response formats
  const categoryList = React.useMemo(() => {
    let list: any[] = [];
    if (Array.isArray(categories)) {
      list = categories;
    } else if (categories && Array.isArray(categories.categories)) {
      list = categories.categories;
    } else if (categories && Array.isArray(categories.data)) {
      list = categories.data;
    }

    // Fallback: extract from products if list is empty or invalid
    if (list.length === 0 && products.length > 0) {
      const uniqueTypes = Array.from(new Set(products.map(p => p.type).filter(Boolean)));
      list = uniqueTypes.map(type => ({ type }));
    }

    return list;
  }, [categories, products]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showFundWalletModal, setShowFundWalletModal] = useState(false);
  const [isCategoriesCollapsed, setIsCategoriesCollapsed] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1: Review, 2: OTP, 3: Success
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<any | null>(null);
  const [checkoutRefId, setCheckoutRefId] = useState<string | null>(null);
  const [productPage, setProductPage] = useState(1);
  const [customerPage, setCustomerPage] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState("Lagos");
  const productLimit = 12;
  const customersPerPage = 7;
  const customerFetchLimit = 10000;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCustomerPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Delivery details states
  const [deliveryDetails, setDeliveryDetails] = useState("");
  const [deliveryContact, setDeliveryContact] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [allFees, setAllFees] = useState<FeeItem[]>([]);
  const [hasOrderedBefore, setHasOrderedBefore] = useState(true);
  const [walletBalance, setWalletBalance] = useState(0);
  const [promoCode, setPromoCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [promoFinalAmount, setPromoFinalAmount] = useState<number | null>(null);
  const [promoDetails, setPromoDetails] = useState<any | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  const toDateInputValue = (d: Date) => {
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const minDeliveryDate = React.useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toDateInputValue(tomorrow);
  }, []);

  const resetPromoState = () => {
    setPromoCode("");
    setDiscountAmount(0);
    setPromoMessage(null);
    setPromoFinalAmount(null);
    setPromoDetails(null);
  };

  const currentFee = allFees.find(
    (f) => f.stateLocation?.toLowerCase() === selectedLocation.toLowerCase()
  ) || allFees.find((f) => f.stateLocation?.toLowerCase() === "regular");

  const isFreeFirstOrder = currentFee?.isFreeDeliveryOnFirstOrder && !hasOrderedBefore;
  const deliveryCharge = isFreeFirstOrder ? 0 : (currentFee?.deliveryFee || 0);
  const serviceCharge = currentFee?.serviceFee || 500;

  const subtotalAmount = Number(totalAmount) || 0;
  const deliveryChargeAmount = Number(deliveryCharge) || 0;
  const serviceChargeAmount = Number(serviceCharge) || 0;
  const discountAmountValue = Number(discountAmount) || 0;
  const promoSubtotalAmount =
    promoFinalAmount !== null
      ? Number(promoFinalAmount) || 0
      : Math.max(0, subtotalAmount - discountAmountValue);

  const grandTotal = Math.max(
    0,
    promoSubtotalAmount + deliveryChargeAmount + serviceChargeAmount,
  );

  const walletShortfall = React.useMemo(
    () => Math.max(0, grandTotal - walletBalance),
    [grandTotal, walletBalance],
  );

  const fundModalTotalsRef = useRef({ grandTotal, walletBalance });

  useEffect(() => {
    if (selectedCustomer) {
      resetPromoState();
      setDeliveryDetails(selectedCustomer.address || "");
      setDeliveryContact(selectedCustomer.phoneNumber || "");
      // Default to 7 days from now
      const defaultDate = toDateInputValue(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      );
      setDeliveryDate(defaultDate);

      const customerId = selectedCustomer.customerId;

      // Check if user has ordered before
      dispatch(checkIfUserHasOrder(customerId))
        .unwrap()
        .then((res) => {
          setHasOrderedBefore(res.hasOrderedBefore);
        })
        .catch((err) => {
          console.error("Error checking order history:", err);
          setHasOrderedBefore(true); // Fallback to charging if check fails
        });

      // Get full customer details for wallet balance
      dispatch(getCustomerByuserIdThunk(customerId))
        .unwrap()
        .then((res) => {
          if (res.customer) {
            setWalletBalance(res.customer.virtualAccount?.walletbalance || 0);
            setDeliveryDetails(res.customer.deliveryAddress || res.customer.address || "");
            setDeliveryContact(res.customer.deliveryContact || res.customer.phoneNumber || "");
            if (res.customer.deliveryDateOption) {
              setDeliveryDate(res.customer.deliveryDateOption.split('T')[0]);
            }
            if (res.customer.currentState) {
              // Map API state to UI location if needed, for now just matching case
              const apiState = res.customer.currentState.toLowerCase();
              if (apiState === "lagos") setSelectedLocation("Lagos");
              else if (apiState === "oyo") setSelectedLocation("Oyo");
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching customer details:", err);
        });
    }
  }, [selectedCustomer, dispatch]);

  useEffect(() => {
    dispatch(
      getAccountOfficerAssignedCustomersWithRisk({
        page: 1,
        limit: customerFetchLimit,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllProducts());
    dispatch(getAllProductType());

    // Fetch all fees
    dispatch(getAllFees())
      .unwrap()
      .then((res) => {
        setAllFees(res.allfees || []);
      })
      .catch((err) => {
        console.error("Error fetching fees:", err);
      });
  }, [dispatch]);

  // Handle URL persistence
  useEffect(() => {
    const customerId = searchParams.get("customerId");
    if (customerId && assignedCustomers.length > 0) {
      const customer = assignedCustomers.find(
        (c) => c.customerId === customerId,
      );
      if (customer) {
        setSelectedCustomer(customer);
        setShowCatalog(true);
      }
    } else if (!customerId) {
      setShowCatalog(false);
      setSelectedCustomer(null);
    }
  }, [searchParams, assignedCustomers]);

  const formatNaira = (value: number) => {
    if (!value || value === 0) return "0";
    return `₦${Number(value).toLocaleString()}`;
  };
  // Client-side search filter (server search is not supported)
  const filteredCustomers = React.useMemo(() => {
    const q = debouncedSearchTerm.trim().toLowerCase();
    if (!q) return assignedCustomers;
    return assignedCustomers.filter((c: any) => {
      const fullName = `${c.firstName || ""} ${c.lastName || ""}`.trim().toLowerCase();
      const email = String(c.email || "").toLowerCase();
      return fullName.includes(q) || email.includes(q);
    });
  }, [assignedCustomers, debouncedSearchTerm]);

  // Client-side pagination on filtered results
  const totalCustomerPages = Math.ceil(filteredCustomers.length / customersPerPage);
  const paginatedCustomers = filteredCustomers.slice(
    (customerPage - 1) * customersPerPage,
    customerPage * customersPerPage,
  );


  const getCustomerStatusBadgeClass = (status: unknown) => {
    const s = String(status || "active").toLowerCase();
    if (s === "active") {
      return "bg-green-50 text-green-700 border border-green-100";
    }
    if (s === "inactive") {
      return "bg-red-50 text-red-700 border border-red-100";
    }
    return "bg-gray-50 text-gray-700 border border-gray-200";
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name
      .toLowerCase()
      .includes(productSearchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || p.type === selectedCategory;
    const matchesLocation = p.location?.toLowerCase() === selectedLocation.toLowerCase();
    return matchesSearch && matchesCategory && matchesLocation;
  });

  const getProductPrice = (product: any) => {
    if (Array.isArray(product.priceTag)) {
      const locationPrice = product.priceTag.find(
        (tag: any) => tag.location?.toLowerCase() === selectedLocation.toLowerCase()
      );
      if (locationPrice) return Number(locationPrice.amount);
    }
    return Number(product.price);
  };

  const totalProductPages = Math.ceil(filteredProducts.length / productLimit);
  const paginatedProducts = filteredProducts.slice(
    (productPage - 1) * productLimit,
    productPage * productLimit,
  );

  useEffect(() => {
    setProductPage(1);
  }, [productSearchTerm, selectedCategory, selectedLocation]);

  useEffect(() => {
    if (selectedCustomer) {
      dispatch(getCartItems(selectedCustomer.customerId));
    }
  }, [selectedCustomer, dispatch]);

  const handleSavePreferences = async (newDate?: string, newContact?: string) => {
    if (!selectedCustomer) return;

    try {
      await dispatch(updateDeliveryPreferencesThunk({
        customeruserId: selectedCustomer.customerId,
        data: {
          deliveryContact: newContact || deliveryContact,
          deliveryDateOption: new Date(newDate || deliveryDate).toISOString(),
          currentState: selectedLocation
        }
      })).unwrap();
      toast.success("Delivery preferences updated");
    } catch (error: any) {
      toast.error(error || "Failed to update preferences");
    }
  };

  const handleValidatePromoCode = async () => {
    if (!promoCode) {
      toast.error("Please enter a promo code");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Please select a customer first");
      return;
    }

    setIsValidatingPromo(true);
    try {
      const res = await dispatch(
        validatePromoCodeThunk({
          customerId: selectedCustomer.customerId,
          data: {
            promoCode: promoCode,
            orderAmount: totalAmount,
          },
        }),
      ).unwrap();

      const promoData: any = (res as any)?.data ?? null;
      setPromoDetails(promoData);

      const scope = String(promoData?.scope || "").toLowerCase();
      const value = Number(promoData?.value);
      const subtotal = Number(totalAmount) || 0;

      let nextDiscount = 0;
      if (Number.isFinite(value) && value > 0) {
        if (scope.includes("fixed")) {
          nextDiscount = Math.min(subtotal, value);
        } else if (scope.includes("percent")) {
          nextDiscount = Math.min(subtotal, (subtotal * value) / 100);
        }
      }

      setPromoFinalAmount(null);
      setPromoMessage((res as any)?.message || null);
      setDiscountAmount(nextDiscount);

      toast.success("Promo code applied");
    } catch (error: any) {
      toast.error(error || "Invalid promo code");
      setDiscountAmount(0);
      setPromoMessage(null);
      setPromoFinalAmount(null);
      setPromoDetails(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleSelectCustomer = (customer: any) => {
    setSearchParams({ customerId: customer.customerId });
  };

  const handleCloseCatalog = () => {
    setSearchParams({});
    dispatch(clearCart());
    setShowCart(false);
    setShowCheckout(false);
    setCheckoutStep(1);
    setOtp("");
    setCreatedOrder(null);
    resetPromoState();
  };

  useEffect(() => {
    if (showCheckout) return;
    setCheckoutStep(1);
    setOtp("");
    setCreatedOrder(null);
    setCheckoutRefId(null);
    resetPromoState();
  }, [showCheckout]);

  useEffect(() => {
    if (!showCheckout) {
      setShowFundWalletModal(false);
    }
  }, [showCheckout]);

  useEffect(() => {
    if (!showFundWalletModal) {
      fundModalTotalsRef.current = { grandTotal, walletBalance };
      return;
    }
    const prev = fundModalTotalsRef.current;
    if (prev.grandTotal !== grandTotal || prev.walletBalance !== walletBalance) {
      toast("Cart or promo changed. Funding amount was updated.");
      fundModalTotalsRef.current = { grandTotal, walletBalance };
    }
  }, [showFundWalletModal, grandTotal, walletBalance]);

  const handleAddToCart = async (product: any) => {
    if (!selectedCustomer) return;

    setIsLoading(true);
    try {
      const res = await dispatch(addToCartThunk({
        customerId: selectedCustomer.customerId,
        data: {
          productId: product._id,
          quantity: 1
        }
      })).unwrap();

      toast.success(res.message || `${product.name} added to cart`);
      dispatch(getCartItems(selectedCustomer.customerId));
    } catch (error: any) {
      toast.error(error || "Failed to add to cart");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuantity = async (productId: string, qty: number, currentQty: number) => {
    if (!selectedCustomer) return;

    if (qty <= 0) {
      // If quantity reaches 0, find the item and delete it
      const itemToDelete = cartItems.find(item => item.productId === productId);
      if (itemToDelete) {
        handleDeleteCartItem(itemToDelete._id);
      }
      return;
    }

    setIsLoading(true);
    try {
      const diff = qty - currentQty;
      const res = await dispatch(updateCartItemThunk({
        customerId: selectedCustomer.customerId,
        data: {
          productId,
          quantity: diff
        }
      })).unwrap();

      toast.success(res.message || "Cart updated");
      dispatch(getCartItems(selectedCustomer.customerId));
    } catch (error: any) {
      toast.error(error || "Failed to update cart");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCartItem = async (itemId: string) => {
    if (!selectedCustomer) return;

    setIsLoading(true);
    try {
      const res = await dispatch(deleteCartItemThunk({
        customerId: selectedCustomer.customerId,
        data: { itemId }
      })).unwrap();

      toast.success(res.message || "Item removed from cart");
      dispatch(getCartItems(selectedCustomer.customerId));
    } catch (error: any) {
      toast.error(error || "Failed to delete item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setShowCheckout(true);
    setCheckoutStep(1);
  };

  const handleWalletFundComplete = async () => {
    if (!selectedCustomer) return;
    try {
      const res = await dispatch(
        getCustomerByuserIdThunk(selectedCustomer.customerId),
      ).unwrap();
      const nextBalance = res.customer?.virtualAccount?.walletbalance || 0;
      if (res.customer) {
        setWalletBalance(nextBalance);
      }
      setShowFundWalletModal(false);
      if (nextBalance >= grandTotal) {
        toast.success("Wallet balance covers this order. Continue checkout.");
      } else {
        toast.success(
          "Balance refreshed. Add more funds if the wallet is still short.",
        );
      }
    } catch {
      toast.error("Could not refresh wallet balance");
    }
  };

  const handleRequestOtp = async () => {
    if (!selectedCustomer?.email) {
      toast.error("Customer email not found");
      return;
    }

    if (checkoutStep === 1) {
      if (walletBalance < grandTotal) {
        setShowFundWalletModal(true);
        return;
      }
    }

    setIsLoading(true);
    try {
      const res = await dispatch(sendConfirmationOtp({
        email: selectedCustomer.email
      })).unwrap();

      setCheckoutStep(2);
      toast.success(res.message || "OTP sent to customer's email");
    } catch (error: any) {
      toast.error(error || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const submitOrder = async () => {
    if (!selectedCustomer) {
      toast.error("No customer selected");
      return;
    }

    if (!deliveryDate) {
      toast.error("Please select a delivery date");
      return;
    }
    if (deliveryDate < minDeliveryDate) {
      toast.error("Delivery date must be from tomorrow onward");
      return;
    }

    if (otp.length !== 4) {
      toast.error("Please enter a 4-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      // 1. Update Delivery Preferences
      await dispatch(updateDeliveryPreferencesThunk({
        customeruserId: selectedCustomer.customerId,
        data: {
          deliveryContact: deliveryContact,
          deliveryDateOption: new Date(deliveryDate).toISOString(),
          currentState: selectedLocation
        }
      })).unwrap();

      await dispatch(verifyOtp({
        email: selectedCustomer.email,
        otp: otp
      })).unwrap();

      const payload = checkoutRefId
        ? { checkoutReference: checkoutRefId }
        : {
          deliveryDetails: deliveryDetails || "Zone 5,lane 2,Ekerin Ologuneru,Ibadan, Oyo",
          deliveryFee: deliveryCharge,
          serviceFee: serviceCharge,
          deliveryContact: deliveryContact || "+2348039203637",
          deliveryDateOption: new Date(deliveryDate).toISOString(),
          orderType: "outright",
        };

      const res = await dispatch(createOrderThunk({
        customerId: selectedCustomer.customerId,
        data: payload
      })).unwrap();

      toast.success("Order created successfully!");
      setCreatedOrder(res || null);
      setCheckoutStep(3);
    } catch (error: any) {
      console.error("Order submission error:", error);
      toast.error(error || "Error submitting order");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-8">
      {!showCatalog ? (
        <div className="max-w-4xl mx-auto">
          <header className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              Create New Order
            </h1>
            <p className="text-gray-500 text-sm">
              Select a customer to start placing an order
            </p>
          </header>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                <input
                  type="text"
                  placeholder="Search customers by name or email..."
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 transition-all font-medium"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/50 text-gray-400 text-[10px] font-black uppercase tracking-widest border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4">Customer</th>
                    <th className="px-6 py-4 hidden md:table-cell">Contact</th>
                    <th className="px-6 py-4 hidden lg:table-cell">Status</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {assignedCustomersStatus === "loading" ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : filteredCustomers.length > 0 ? (
                    paginatedCustomers.length > 0 ? (
                      paginatedCustomers.map((customer) => (
                        <tr
                          key={customer.customerId}
                          className="hover:bg-gray-50/80 transition-all group cursor-pointer"
                          onClick={() => handleSelectCustomer(customer)}
                        >
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-green-600 font-black text-xs border border-green-100 shadow-sm group-hover:scale-110 transition-transform">
                                {customer.firstName?.charAt(0) || ""}
                                {customer.lastName?.charAt(0) || ""}
                              </div>
                              <div>
                                <h3 className="font-bold text-gray-900 text-sm group-hover:text-green-700 transition-colors">
                                  {customer.firstName} {customer.lastName}
                                </h3>
                                <p className="text-[10px] text-gray-400 font-medium md:hidden">
                                  {customer.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5 hidden md:table-cell">
                            <div className="space-y-0.5">
                              <p className="text-sm font-medium text-gray-700">{customer.email}</p>
                              <p className="text-xs text-gray-400">{customer.phoneNumber || "No Phone"}</p>
                            </div>
                          </td>
                          <td className="px-6 py-5 hidden lg:table-cell">
                            <span
                              className={cn(
                                "px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest",
                                getCustomerStatusBadgeClass(customer.status),
                              )}
                            >
                              {customer.status || "active"}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-right">
                            <button
                              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-green-600 transition-all shadow-md shadow-gray-200"
                            >
                              <FiPlus className="text-sm" />
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-12 text-center text-gray-400 font-medium"
                        >
                          No customers found matching your search.
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-12 text-center text-gray-400 font-medium"
                      >
                        No customers found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {totalCustomerPages > 1 && (
              <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                  Page <span className="text-gray-900">{customerPage}</span> of {totalCustomerPages}
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={customerPage === 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomerPage(prev => Math.max(1, prev - 1));
                    }}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-30 hover:bg-gray-50 shadow-sm"
                  >
                    Prev
                  </button>
                  <button
                    disabled={customerPage === totalCustomerPages}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomerPage(prev => Math.min(totalCustomerPages, prev + 1));
                    }}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-30 hover:bg-gray-50 shadow-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-screen -m-4 md:-m-8 bg-white relative">
          {/* Header */}
          <header className="h-16 border-b border-gray-100 px-6 flex items-center justify-between bg-white sticky top-0 z-50">
            <div className="flex items-center gap-4">
              <button
                onClick={handleCloseCatalog}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FiArrowLeft className="text-xl text-gray-600" />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h2 className="font-bold text-gray-900 truncate">
                    Store: {selectedCustomer?.firstName}{" "}
                    {selectedCustomer?.lastName}
                  </h2>
                  <span className="inline-flex shrink-0 items-center gap-2.5 rounded-xl border border-green-100 bg-green-50/80 px-3.5 py-1.5 text-sm font-black text-green-800 shadow-sm">
                    <span className="text-[11px] font-black uppercase tracking-widest text-green-600/80">
                      Wallet balance
                    </span>
                    <span className="tabular-nums text-base">{formatNaira(walletBalance)}</span>
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1">
                  Mini E-commerce Dashboard for Account Officer
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-600 hover:text-white transition-all"
              >
                <FiShoppingCart className="text-xl" />
                {totalQuantity > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {totalQuantity}
                  </span>
                )}
              </button>
              <button
                onClick={handleCloseCatalog}
                className="p-2 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Product Listing */}
            <main className="flex-1 bg-gray-50/50 p-8 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="mb-8 space-y-8">
                <div className="space-y-6 mb-12">
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                      <h2 className="text-3xl font-black text-gray-900 mb-2">
                        Available Products
                      </h2>
                      <p className="text-sm text-gray-400 font-medium">
                        Select items to add to customer's cart in{" "}
                        <span className="text-green-600 font-bold px-2 py-0.5 bg-green-50 rounded-lg border border-green-100 uppercase tracking-tighter">
                          {selectedLocation}
                        </span>
                      </p>
                    </div>

                    <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-2xl border border-gray-200/50">
                      {["Lagos", "Oyo"].map((loc) => (
                        <button
                          key={loc}
                          onClick={() => setSelectedLocation(loc)}
                          className={cn(
                            "px-6 py-2.5 text-[10px] font-black uppercase tracking-[0.1em] rounded-xl transition-all duration-300",
                            selectedLocation === loc
                              ? "bg-white text-gray-900 shadow-md shadow-gray-200/50 border border-gray-100"
                              : "text-gray-400 hover:text-gray-600"
                          )}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative group max-w-2xl">
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                      <FiSearch className="text-gray-400 group-focus-within:text-green-500 transition-colors text-xl" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search products by name, type, or measurement..."
                      className="w-full pl-14 pr-6 py-5 bg-white border-2 border-gray-100 rounded-[2rem] focus:outline-none focus:border-green-500/30 focus:ring-4 focus:ring-green-500/5 transition-all text-base shadow-sm font-medium placeholder:text-gray-300"
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                    />
                    {productSearchTerm && (
                      <button
                        onClick={() => setProductSearchTerm("")}
                        className="absolute inset-y-0 right-5 flex items-center text-gray-300 hover:text-gray-500 transition-colors"
                      >
                        <FiX className="text-lg" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Categories Grid - PROMINENT DISPLAY */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-black text-gray-900 flex items-center gap-3 text-lg">
                      <FiFilter className="text-[#00A651]" />
                      Browse by Category
                    </h3>
                    <button
                      onClick={() => setIsCategoriesCollapsed(!isCategoriesCollapsed)}
                      className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider"
                    >
                      {isCategoriesCollapsed ? (
                        <>
                          Show All <FiChevronDown className="text-lg" />
                        </>
                      ) : (
                        <>
                          Collapse <FiChevronUp className="text-lg" />
                        </>
                      )}
                    </button>
                  </div>

                  <div className={cn(
                    "overflow-hidden transition-all duration-300 ease-in-out",
                    isCategoriesCollapsed ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
                  )}>
                    {productStatus === "loading" && categoryList.length === 0 ? (
                      <div className="flex flex-wrap gap-3 pb-2">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="h-12 w-32 bg-white border border-gray-100 animate-pulse rounded-2xl"></div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-3 pb-2">
                        <button
                          onClick={() => setSelectedCategory("All")}
                          className={cn(
                            "px-6 py-3 rounded-2xl text-xs font-black transition-all border-2 tracking-widest uppercase",
                            selectedCategory === "All"
                              ? "bg-[#00A651] text-white border-[#00A651] shadow-lg shadow-green-100/50"
                              : "bg-white text-gray-500 border-gray-100 hover:border-green-200"
                          )}
                        >
                          ALL PRODUCTS
                        </button>
                        {categoryList.map((cat: any) => {
                          const catName = cat.type || cat.name || (typeof cat === 'string' ? cat : "Unknown");
                          return (
                            <button
                              key={cat._id || catName}
                              onClick={() => setSelectedCategory(catName)}
                              className={cn(
                                "px-6 py-3 rounded-2xl text-xs font-black transition-all border-2 tracking-widest uppercase",
                                selectedCategory === catName
                                  ? "bg-[#00A651] text-white border-[#00A651] shadow-lg shadow-green-100/50"
                                  : "bg-white text-gray-500 border-gray-100 hover:border-green-200"
                              )}
                            >
                              {catName}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {productStatus === "loading" ? (
                <div className="relative">
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
                    <div className="w-10 h-10 border-[3px] border-gray-200 border-t-[#00A651] rounded-full animate-spin" />
                    <p className="mt-3 text-xs font-bold text-gray-400 uppercase tracking-widest">Loading products</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 opacity-40">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-white rounded-2xl p-4 border border-gray-100 animate-pulse flex flex-col"
                      >
                        <div className="aspect-square rounded-xl bg-gray-100 mb-4" />
                        <div className="h-3.5 bg-gray-100 rounded-lg w-3/4 mb-2" />
                        <div className="h-3 bg-gray-100 rounded-lg w-1/2 mb-4" />
                        <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
                          <div className="space-y-1.5">
                            <div className="h-2 bg-gray-100 rounded w-10" />
                            <div className="h-4 bg-gray-100 rounded w-16" />
                          </div>
                          <div className="w-10 h-10 bg-gray-100 rounded-xl" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : paginatedProducts.length > 0 ? (
                <div className="space-y-10">
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                    {paginatedProducts.map((product) => (
                      <div
                        key={product._id}
                        className="bg-white rounded-2xl p-4 border border-gray-100 hover:shadow-xl hover:shadow-gray-200/50 transition-all group flex flex-col"
                      >
                        <div className="aspect-square rounded-xl bg-gray-50 mb-4 overflow-hidden relative">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-2 right-2 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-[10px] font-bold text-green-600 border border-green-100">
                            {product.type}
                          </div>
                        </div>
                        <h4 className="font-bold text-gray-900 text-sm mb-1 line-clamp-2">
                          {product.name}
                        </h4>
                        <p className="text-xs text-gray-400 mb-3">
                          {product.measurement}
                        </p>
                        <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-50">
                          <div className="flex flex-col">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                              Price
                            </span>
                            <span className="text-base font-black text-gray-900">
                              {formatNaira(getProductPrice(product))}
                            </span>
                          </div>
                          <button
                            onClick={() => handleAddToCart(product)}
                            className="p-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all shadow-md shadow-green-100 active:scale-95 group"
                            title="Add to cart"
                          >
                            <FiPlus className="text-lg group-hover:scale-125 transition-transform" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Product Pagination */}
                  {totalProductPages > 1 && (
                    <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                        Showing page <span className="text-gray-900">{productPage}</span> of {totalProductPages}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setProductPage(prev => Math.max(1, prev - 1))}
                          disabled={productPage === 1}
                          className="px-4 py-2 text-xs font-black uppercase tracking-widest bg-white border border-gray-100 rounded-xl disabled:opacity-50 hover:bg-gray-50 transition-all"
                        >
                          Prev
                        </button>
                        {[...Array(totalProductPages)].map((_, i) => {
                          const p = i + 1;
                          // Show first, last, and current ± 1
                          if (
                            p === 1 ||
                            p === totalProductPages ||
                            (p >= productPage - 1 && p <= productPage + 1)
                          ) {
                            return (
                              <button
                                key={p}
                                onClick={() => setProductPage(p)}
                                className={cn(
                                  "w-8 h-8 text-[10px] font-black rounded-xl border transition-all",
                                  productPage === p
                                    ? "bg-[#00A651] text-white border-[#00A651] shadow-lg shadow-green-100"
                                    : "bg-white text-gray-400 border-gray-100 hover:border-green-200"
                                )}
                              >
                                {p}
                              </button>
                            );
                          } else if (
                            p === productPage - 2 ||
                            p === productPage + 2
                          ) {
                            return <span key={p} className="text-gray-300">...</span>;
                          }
                          return null;
                        })}
                        <button
                          onClick={() => setProductPage(prev => Math.min(totalProductPages, prev + 1))}
                          disabled={productPage === totalProductPages}
                          className="px-4 py-2 text-xs font-black uppercase tracking-widest bg-white border border-gray-100 rounded-xl disabled:opacity-50 hover:bg-gray-50 transition-all"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <FiShoppingBag className="text-6xl mb-4 opacity-20" />
                  <p className="font-bold">No products found</p>
                </div>
              )}
            </main>
          </div>

          {/* Cart Sidebar Overlay */}
          {showCart && (
            <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex justify-end">
              <div className="w-full max-w-md bg-white h-full shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
                <header className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                    <FiShoppingCart className="text-green-600" />
                    Shopping Cart
                  </h3>
                  <button
                    onClick={() => setShowCart(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <FiX className="text-xl" />
                  </button>
                </header>

                <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
                  {cartItems.length > 0 ? (
                    cartItems.map((item) => (
                      <div key={item._id} className="flex gap-4 items-center">
                        <div className="w-16 h-16 rounded-xl bg-gray-50 overflow-hidden flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 text-sm truncate">
                            {item.name}
                          </h4>
                          <h4 className="text-xs text-gray-400">
                            {item.measurement}
                          </h4>
                          <p className="text-sm font-black text-green-600 mt-1">
                            {formatNaira(Number(item.pricePerQuantity))}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <button
                            onClick={() => handleDeleteCartItem(item._id)}
                            className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                            title="Remove item"
                          >
                            <FiTrash2 className="text-sm" />
                          </button>
                          <div className="flex items-center gap-3 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item.productId, item.quantity - 1, item.quantity)
                              }
                              className="text-gray-500 hover:text-green-600 transition-colors"
                            >
                              <FiMinus className="text-xs" />
                            </button>
                            <span className="text-sm font-black text-gray-900 w-4 text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item.productId, item.quantity + 1, item.quantity)
                              }
                              className="text-gray-500 hover:text-green-600 transition-colors"
                            >
                              <FiPlus className="text-xs" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <FiShoppingCart className="text-6xl mb-4 opacity-20" />
                      <p className="font-bold">Your cart is empty</p>
                    </div>
                  )}
                </div>

                {cartItems.length > 0 && (
                  <footer className="p-6 border-t border-gray-100 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-gray-500 font-bold uppercase text-xs tracking-widest">
                        Subtotal
                      </span>
                      <span className="text-xl font-black text-gray-900">
                        {formatNaira(totalAmount)}
                      </span>
                    </div>
                    <button
                      onClick={handleProceedToCheckout}
                      className="w-full py-4 bg-green-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-green-700 transition-all shadow-lg shadow-green-200"
                    >
                      Proceed to Checkout
                    </button>
                  </footer>
                )}
              </div>
            </div>
          )}

          {/* Checkout Modal */}
          {showCheckout && (
            <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-md flex items-end sm:items-center justify-center">
              <div className="bg-white w-full sm:max-w-xl lg:max-w-2xl h-[92vh] sm:h-auto sm:max-h-[86vh] rounded-t-[2.25rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200 flex flex-col">
                {/* Header */}
                <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 border-b border-gray-100 bg-white/90 backdrop-blur-md sticky top-0 z-10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
                        Checkout
                      </h3>
                      <p className="text-[10px] sm:text-xs text-gray-400 font-black uppercase tracking-[0.18em] mt-2">
                        Step {checkoutStep} of 3
                      </p>
                    </div>
                    <button
                      onClick={() => setShowCheckout(false)}
                      className="p-2 -mr-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                      aria-label="Close checkout"
                    >
                      <FiX className="text-xl text-gray-700" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 no-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">

                  {checkoutStep === 1 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      {/* Customer Details Section */}
                      <div className="bg-green-50/30 p-5 rounded-3xl border border-green-100/50">
                        <div className="flex justify-between items-center mb-5">
                          <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                            Customer Details
                          </span>
                          <button
                            onClick={() => setIsEditingAddress(!isEditingAddress)}
                            className="text-[10px] font-black text-green-600 flex items-center gap-1 hover:underline uppercase tracking-widest"
                          >
                            Edit Customer Detail <FiEdit3 />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-white rounded-xl text-green-600 border border-green-50 shadow-sm">
                              <FiUser className="text-sm" />
                            </div>
                            <span className="text-sm font-bold text-gray-900 mt-1.5">
                              {selectedCustomer?.firstName} {selectedCustomer?.lastName}
                            </span>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-white rounded-xl text-green-600 border border-green-50 shadow-sm">
                              <FiMapPin className="text-sm" />
                            </div>
                            {isEditingAddress ? (
                              <textarea
                                className="flex-1 bg-white border border-green-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/10 min-h-[80px] resize-none"
                                value={deliveryDetails}
                                onChange={(e) => setDeliveryDetails(e.target.value)}
                                onBlur={() => setIsEditingAddress(false)}
                                autoFocus
                              />
                            ) : (
                              <span className="text-sm font-medium text-gray-600 mt-1 leading-relaxed">
                                {deliveryDetails || "No address provided"}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white rounded-xl text-green-600 border border-green-50 shadow-sm">
                                <FiPhone className="text-sm" />
                              </div>
                              {isEditingPhone ? (
                                <input
                                  type="tel"
                                  className="bg-white border border-green-200 rounded-xl p-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/10"
                                  value={deliveryContact}
                                  onChange={(e) => setDeliveryContact(e.target.value)}
                                  onBlur={() => {
                                    setIsEditingPhone(false);
                                    handleSavePreferences(undefined, deliveryContact);
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span className="text-sm font-medium text-gray-600">
                                  {deliveryContact || "No contact provided"}
                                </span>
                              )}
                            </div>
                            {!isEditingPhone && (
                              <button
                                onClick={() => setIsEditingPhone(true)}
                                className="text-[10px] font-black text-green-600 flex items-center gap-1 hover:underline uppercase tracking-widest"
                              >
                                Add <FiPlus />
                              </button>
                            )}
                          </div>

                          <div className="bg-red-50 p-3 rounded-2xl border border-red-100/50 mt-4">
                            <p className="text-[10px] text-red-500 font-medium leading-relaxed">
                              Ensure numbers are reachable at delivery to avoid cancellation or extra fees
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Details Section */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                            Delivery Details
                          </span>
                          <button className="text-[10px] font-black text-green-600 flex items-center gap-1 hover:underline uppercase tracking-widest">
                            Set a date from today upward <FiPlus />
                          </button>
                        </div>

                        <div>
                          <DatePicker
                            value={deliveryDate}
                            min={minDeliveryDate}
                            placeholder="Select delivery date"
                            onChange={(val) => {
                              setDeliveryDate(val);
                              handleSavePreferences(val);
                            }}
                          />
                          <div className="mt-1.5 text-center">
                            <span className="text-[9px] text-gray-300 font-bold uppercase tracking-widest">Door Delivery</span>
                          </div>
                        </div>
                      </div>

                      {/* Item Charges Section */}
                      <div className="bg-green-50/20 p-5 rounded-3xl border border-green-100/50">
                        <div className="flex justify-between items-center mb-6">
                          <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                            Items Charges
                          </span>
                          <button
                            onClick={() => setShowCheckout(false)}
                            className="text-[10px] font-black text-green-600 flex items-center gap-1 hover:underline uppercase tracking-widest"
                          >
                            Change <FiChevronUp className="rotate-90" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500 font-medium">Subtotal</span>
                            <span className="text-sm text-gray-900 font-bold">{formatNaira(totalAmount)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500 font-medium">Delivery Charge</span>
                            <span className="text-sm text-gray-900 font-bold">{formatNaira(deliveryCharge)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500 font-medium">Service Charge</span>
                            <span className="text-sm text-gray-900 font-bold">{formatNaira(serviceCharge)}</span>
                          </div>
                          {discountAmount > 0 && (
                            <div className="flex justify-between items-center text-red-500">
                              <span className="text-sm font-medium italic">Discount Applied</span>
                              <span className="text-sm font-bold">-{formatNaira(discountAmount)}</span>
                            </div>
                          )}
                          <div className="pt-4 border-t border-green-100 flex justify-between items-center">
                            <span className="text-base font-black text-gray-900">Total</span>
                            <span className="text-base font-black text-gray-900">{formatNaira(grandTotal)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Promo Code Section */}
                      <div className="bg-orange-50/30 p-5 rounded-3xl border border-orange-100/50">
                        <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest block mb-4">
                          Apply Promo Code
                        </span>
                        {discountAmount > 0 ? (
                          <div className="rounded-2xl border border-green-100 bg-white p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                                  Promo code
                                </p>
                                <p className="text-sm font-black text-gray-900 mt-1 truncate line-through decoration-2 decoration-green-600/50">
                                  {promoDetails?.promoCode || promoCode}
                                </p>
                              </div>
                              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-green-50 text-green-700 border border-green-100 text-[11px] font-black">
                                <FiCheckCircle className="text-base" />
                                Promo code applied
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Enter Code (e.g. CHISOM883)"
                              className="flex-1 bg-white border border-orange-100 rounded-2xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500/10 uppercase placeholder:text-gray-300"
                              value={promoCode}
                              onChange={(e) => setPromoCode(e.target.value)}
                            />
                            <button
                              onClick={handleValidatePromoCode}
                              disabled={isValidatingPromo || !promoCode}
                              className="px-6 bg-orange-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-orange-600 transition-all disabled:opacity-50"
                            >
                              {isValidatingPromo ? "..." : "Apply"}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Wallet payment */}
                      <div className="space-y-4">
                        <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest block">
                          Payment
                        </span>
                        <div className="flex flex-col items-start p-4 rounded-2xl border-2 border-[#00A651] bg-green-50/30 ring-4 ring-green-500/5 text-left">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-5 h-5 rounded-full border-2 border-[#00A651] bg-[#00A651] flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-white" />
                            </div>
                            <span className="text-[11px] font-black text-gray-700 uppercase tracking-tighter">
                              Wallet balance
                            </span>
                          </div>
                          <span className="text-sm font-black text-gray-900 ml-8">
                            {formatNaira(walletBalance)}
                          </span>
                        </div>
                      </div>

                      {/* Footer Button */}
                      <button
                        onClick={handleRequestOtp}
                        disabled={isLoading}
                        className="w-full py-5 bg-[#00A651] text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-lime-600 transition-all shadow-xl shadow-lime-100 mt-4 active:scale-95"
                      >
                        {isLoading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                        ) : (
                          "Confirm Checkout"
                        )}
                      </button>
                    </div>
                  )}

                  {checkoutStep === 2 && (
                    <div className="space-y-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="bg-orange-50 p-6 rounded-3xl border border-orange-100">
                        <h4 className="text-lg font-black text-orange-900 mb-2">
                          Validate Order
                        </h4>
                        <p className="text-sm text-orange-700 font-medium leading-relaxed">
                          Enter the 4-digit OTP sent to the customer's email{" "}
                          <span className="font-black text-orange-900 underline decoration-orange-200 decoration-2 underline-offset-4">
                            {selectedCustomer?.email}
                          </span>{" "}
                          to complete the purchase.
                        </p>
                      </div>

                      <div className="flex justify-center">
                        <input
                          type="text"
                          maxLength={4}
                          placeholder="0000"
                          className="w-48 text-center text-4xl font-black tracking-[0.5em] py-4 bg-gray-50 border-b-4 border-green-600 focus:outline-none transition-all"
                          value={otp}
                          onChange={(e) =>
                            setOtp(e.target.value.replace(/\D/g, ""))
                          }
                        />
                      </div>

                      <div className="space-y-4">
                        <button
                          onClick={submitOrder}
                          disabled={isLoading || otp.length < 4}
                          className="w-full py-4 bg-green-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            "Complete Order"
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCheckoutStep(1)}
                          className="text-xs font-black text-gray-400 uppercase hover:text-gray-600"
                        >
                          Back to checkout
                        </button>
                      </div>
                    </div>
                  )}

                  {checkoutStep === 3 && (
                    <div className="py-10 sm:py-12">
                      <div className="relative overflow-hidden rounded-[2rem] border border-gray-100 bg-gradient-to-b from-white to-gray-50 shadow-[0_18px_60px_rgba(17,24,39,0.10)]">
                        <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-green-100/60 blur-2xl" />
                        <div className="absolute -bottom-28 -left-28 h-64 w-64 rounded-full bg-emerald-100/40 blur-2xl" />

                        <div className="relative px-6 sm:px-10 py-10 sm:py-12 text-center">
                          <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white border border-green-100 shadow-sm">
                            <div className="h-12 w-12 rounded-xl bg-green-50 text-green-700 flex items-center justify-center">
                              <FiCheckCircle className="text-3xl" />
                            </div>
                          </div>

                          <h3 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                            Order confirmed
                          </h3>
                          <p className="text-sm text-gray-500 font-medium mt-2 leading-relaxed">
                            Order has been created for{" "}
                            <span className="text-gray-900 font-black">
                              {selectedCustomer?.firstName} {selectedCustomer?.lastName}
                            </span>
                            .
                          </p>

                          {(createdOrder?.orderNumber || createdOrder?.data?.orderNumber || createdOrder?._id) && (
                            <div className="mt-7 mx-auto max-w-md rounded-2xl bg-white/70 backdrop-blur border border-gray-100 px-5 py-4 text-left">
                              <div className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.18em]">
                                    Order number
                                  </p>
                                  <p className="mt-1 text-sm font-black text-gray-900 truncate">
                                    {createdOrder?.data?.orderNumber || createdOrder?.orderNumber || "—"}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.18em]">
                                    Total
                                  </p>
                                  <p className="mt-1 text-sm font-black text-gray-900">
                                    {formatNaira(
                                      createdOrder?.data?.allItemsTotalPrice
                                      ?? createdOrder?.allItemsTotalPrice
                                      ?? grandTotal
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                            <button
                              onClick={handleCloseCatalog}
                              className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gray-900 text-white text-xs font-black uppercase tracking-[0.18em] hover:bg-black transition-all shadow-lg shadow-gray-200 active:scale-[0.99]"
                            >
                              Return to dashboard
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {selectedCustomer && (
            <FundWalletModal
              open={showFundWalletModal}
              customerEmail={selectedCustomer.email}
              customerUserId={selectedCustomer.customerId}
              shortfall={walletShortfall}
              grandTotal={grandTotal}
              walletBalance={walletBalance}
              formatNaira={formatNaira}
              orderPayload={{
                deliveryDetails: deliveryDetails || "Zone 5,lane 2,Ekerin Ologuneru,Ibadan, Oyo",
                deliveryFee: Number(deliveryCharge) || 0,
                serviceFee: Number(serviceCharge) || 0,
                deliveryContact: deliveryContact || "+2348039203637",
                orderType: "outright",
                deliveryDateOption: deliveryDate
                  ? new Date(deliveryDate).toISOString()
                  : "today",
                displayAmount: grandTotal,
                discount: discountAmount,
                gamified: discountAmount > 0
                  ? [{ remarksType: "discount", discountAmountApplied: discountAmount }]
                  : [],
              }}
              onClose={() => setShowFundWalletModal(false)}
              onFundComplete={handleWalletFundComplete}
              onCheckoutRefCreated={setCheckoutRefId}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CreateOrder;
